import { useHttp } from "@inertiajs/react";
import { t } from "@travily/i18n";
import type LocationsSuggestion from "@travily/types/models/LocationsSuggestion";
import type { WhereSelection } from "@ui/components/SearchBar";
import type { StandardAutocompleteItem, StandardAutocompleteSection } from "@ui/components/StandardAutocomplete";
import { useDebouncedCallback } from "@ui/hooks/use-debounced-callback";
import { getCountryName } from "@ui/lib/format-date";
import { useCallback, useMemo, useRef, useState } from "react";
import { requestNearbyBoundingBox } from "@/lib/nearby-bounding-box";
import { MAP_AREA_SUGGESTION_ID } from "@/lib/search-params";
import { type Bbox, bboxToInsideBoundingBox, type MapboxFeatureType } from "@/lib/where-bounding-boxes";
import { isRegionSlug, REGION_BBOXES, type WhereFilter, whereFilterForSuggestion } from "@/lib/where-regions";

interface SearchResponse {
  destinations: LocationsSuggestion[];
  locations: LocationsSuggestion[];
}

function destinationBbox(suggestion: LocationsSuggestion | undefined): Bbox | null {
  const bbox = suggestion?.bbox;
  if (!bbox || bbox.length < 4) return null;
  const [west, south, east, north] = bbox;
  if (west === undefined || south === undefined || east === undefined || north === undefined) return null;
  return [west, south, east, north];
}

function toAutocompleteItem(suggestion: LocationsSuggestion): StandardAutocompleteItem {
  return {
    id: suggestion.mapbox_id,
    name: suggestion.name,
    description: suggestion.destination_id
      ? `${suggestion.name}, ${getCountryName(suggestion.country_code)}`
      : (suggestion.full_address ?? suggestion.name),
  };
}

function toSections(response: SearchResponse): StandardAutocompleteSection[] {
  const showTitles = response.destinations.length > 0 && response.locations.length > 0;
  const sections: StandardAutocompleteSection[] = [];
  if (response.destinations.length > 0) {
    sections.push({
      id: "destinations",
      title: showTitles ? t("components.search_bar.section_destinations") : undefined,
      items: response.destinations.map(toAutocompleteItem),
    });
  }
  if (response.locations.length > 0) {
    sections.push({
      id: "locations",
      title: showTitles ? t("components.search_bar.section_locations") : undefined,
      items: response.locations.map(toAutocompleteItem),
    });
  }
  return sections;
}

export interface UseSearchBarWhereReturn {
  whereProps: {
    value: WhereSelection | null;
    onChange: (value: WhereSelection | null) => void;
    searchValue: string;
    onSearchChange: (value: string) => void;
    searchResults: StandardAutocompleteSection[];
    isSearching: boolean;
  };
  whereBoundsBox: string | null;
  whereBbox: Bbox | null;
  /** Term narrowing for the current area (region slug / country code), or `null`.
   *  Computed regardless of `termFilters` — the overlay serialises it into the URL. */
  whereFilter: WhereFilter | null;
  /** Mark the current Where pill as a map-derived selection (no bbox lookup, no
   *  Searchkit filter from this hook — the map already drives bounds via Configure). */
  markMapArea: (label: string) => void;
  resetWhere: () => void;
}

export interface UseSearchBarWhereOptions {
  /** Mapbox feature types to forward to `/api/locations/search`. Comma-joined
   *  in the query string; omit to let the server-side default apply. */
  types?: readonly MapboxFeatureType[];
  /** The consuming index carries `region`/`country_code` terms, so an area selection
   *  filters by term and its bbox only frames the map. Indexes without those fields
   *  (programs) leave it off and keep filtering geographically. */
  termFilters?: boolean;
  /** Seed the initial Where selection + bbox (+ term filter) — e.g. from URL params when the
   *  mobile search overlay navigates to a section index. Read once on mount (pages remount
   *  on navigation). A null bbox seeds the term filter with no map framing (a country link
   *  carries no box). */
  initial?: { selection: WhereSelection; bbox: Bbox | null; filter?: WhereFilter | null };
}

interface SearchParams {
  q: string;
  types?: string;
}

export function useSearchBarWhere(options?: UseSearchBarWhereOptions): UseSearchBarWhereReturn {
  const typesParam = options?.types?.length ? options.types.join(",") : undefined;
  const termFilters = options?.termFilters ?? false;
  const http = useHttp<SearchParams, SearchResponse>({ q: "" });
  const httpRef = useRef(http);
  httpRef.current = http;

  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState<StandardAutocompleteSection[]>([]);
  const [whereSelection, setWhereSelection] = useState<WhereSelection | null>(options?.initial?.selection ?? null);
  const [whereBbox, setWhereBbox] = useState<Bbox | null>(options?.initial?.bbox ?? null);
  const [whereFilter, setWhereFilter] = useState<WhereFilter | null>(options?.initial?.filter ?? null);
  const suggestionsRef = useRef<Map<string, LocationsSuggestion>>(new Map());

  const fetchResults = useDebouncedCallback(
    useCallback(
      (query: string) => {
        if (query.trim().length < 2) {
          setSearchResults([]);
          return;
        }
        const h = httpRef.current;
        h.cancel();
        h.transform(() => (typesParam ? { q: query, types: typesParam } : { q: query }));
        h.get("/api/locations/search", {
          onSuccess: (response) => {
            const all = [...response.destinations, ...response.locations];
            suggestionsRef.current = new Map(all.map((s) => [s.mapbox_id, s]));
            setSearchResults(toSections(response));
          },
        });
      },
      [typesParam],
    ),
    300,
  );

  const onSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      fetchResults(value);
    },
    [fetchResults],
  );

  const resolveSuggestionBbox = useCallback(async (id: string): Promise<Bbox | null> => {
    if (id === "nearby") return requestNearbyBoundingBox();
    return isRegionSlug(id) ? REGION_BBOXES[id] : null;
  }, []);

  const resetWhere = useCallback(() => {
    setWhereSelection(null);
    setWhereBbox(null);
    setWhereFilter(null);
    setSearchValue("");
    setSearchResults([]);
  }, []);

  const onChange = useCallback(
    (value: WhereSelection | null) => {
      if (value === null) {
        resetWhere();
        return;
      }

      if (value.kind === "destination") {
        const suggestion = suggestionsRef.current.get(value.id);
        setWhereSelection(value);
        setWhereBbox(destinationBbox(suggestion));
        setWhereFilter(suggestion ? whereFilterForSuggestion(suggestion) : null);
        return;
      }

      // Suggestion (region or "nearby"): resolve bbox asynchronously. Set the
      // selection optimistically so the SearchBar pill updates, then clear it if
      // bbox resolution fails (e.g. user denies geolocation).
      setWhereSelection(value);
      setWhereBbox(null);
      setWhereFilter(isRegionSlug(value.id) ? { kind: "region", slug: value.id } : null);
      void resolveSuggestionBbox(value.id).then((bbox) => {
        if (bbox === null) {
          setWhereSelection(null);
          return;
        }
        setWhereBbox(bbox);
      });
    },
    [resetWhere, resolveSuggestionBbox],
  );

  const markMapArea = useCallback((label: string) => {
    setWhereSelection({ kind: "suggestion", id: MAP_AREA_SUGGESTION_ID, name: label });
    setWhereBbox(null);
    setWhereFilter(null);
  }, []);

  // The single term-vs-bbox decision point: a term selection filters by term, so its
  // bbox stays out of the geo filter and only frames the map.
  const whereBoundsBox = useMemo(() => {
    if (termFilters && whereFilter) return null;
    return whereBbox ? bboxToInsideBoundingBox(whereBbox) : null;
  }, [termFilters, whereFilter, whereBbox]);

  return {
    whereProps: {
      value: whereSelection,
      onChange,
      searchValue,
      onSearchChange,
      searchResults,
      isSearching: http.processing,
    },
    whereBoundsBox,
    whereBbox,
    whereFilter,
    markMapArea,
    resetWhere,
  };
}
